import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useChat } from '../ChatEngine';
import { IconBell, IconSparkle } from './Icons';

// Per-route title + subtitle for the top bar. The page bodies no longer repeat
// their own <h1>; the top bar is the single source of the page heading.
const TITLES: Record<string, [string, string]> = {
  '/': ['Accueil', "Votre centre de contrôle — l'activité du jour en un coup d'œil."],
  '/tableaux': ['Tableaux de bord', "Chiffre d'affaires, marges et niveaux de stock."],
  '/chat': ['Assistant', 'Pilotez Odoo en langage naturel.'],
  '/ventes': ['Ventes', 'Devis → commande → livraison → facture → paiement.'],
  '/stocks': ['Stocks', 'Niveaux, réservations et seuils de réapprovisionnement.'],
  '/lots': ['Traçabilité', 'Suivi par lot : reçu de qui, livré à qui, rappel produit.'],
  '/achats': ['Achats', 'Factures fournisseurs et état de réception.'],
  '/journal': ['Journal technique', 'Erreurs et alertes des automatisations n8n / Odoo.'],
  '/conversations': ['Conversations', 'Historique de vos échanges avec l’assistant.'],
  '/admin': ['Administration', 'Gestion des utilisateurs de STM Bot.'],
};

export default function Topbar() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const chat = useChat();
  const [title, sub] = TITLES[pathname] || TITLES[`/${pathname.split('/')[1]}`] || ['STM Partners', ''];
  // Sur un écran quelconque, le bouton ouvre la popin ; depuis la page plein
  // écran, il fait l'inverse — détacher la conversation pour libérer l'écran.
  // Dans les deux cas c'est la même conversation, elle n'est jamais rechargée.
  const onChatPage = pathname === '/chat';

  // Pastille du Journal : elle était posée EN DUR, donc allumée en permanence — elle ne
  // signalait rien. On relit le compteur des entrées ouvertes à chaque changement d'écran
  // (une requête SQLite, pas Odoo), ce qui la rallume après un incident et l'éteint dès que
  // les entrées sont marquées résolues.
  const [journal, setJournal] = useState(0);
  useEffect(() => {
    let vivant = true;
    api.getLogs({ scope: 'open', limit: 1 })
      .then((r) => { if (vivant) setJournal(r.openCount); })
      .catch(() => { /* la pastille n'est pas critique : on la laisse éteinte */ });
    return () => { vivant = false; };
  }, [pathname]);

  function onAiClick() {
    // Depuis un écran : conversation NEUVE (on vient poser une question sur ce
    // qu'on a sous les yeux). Depuis `/chat` : c'est un détachement, on garde la
    // conversation en cours.
    chat.openDock(onChatPage ? undefined : { fresh: true });
    if (onChatPage) nav('/');
  }

  return (
    <header className="app-topbar">
      <div className="app-topbar-title">
        <h1>{title}</h1>
        {sub && <p>{sub}</p>}
      </div>
      <div className="app-topbar-actions">
        <button
          className="topbar-icon-btn"
          title={journal > 0 ? `Journal technique — ${journal} entrée${journal > 1 ? 's' : ''} à traiter` : 'Journal technique'}
          onClick={() => nav('/journal')}
        >
          <IconBell size={18} />
          {journal > 0 && <span className="dot" />}
        </button>
        <button
          className="topbar-ai-btn"
          onClick={onAiClick}
          title={onChatPage ? 'Continuer en popin et revenir à l\'accueil' : "Demander à l'IA"}
        >
          <IconSparkle size={16} />
          <span className="topbar-ai-label">{onChatPage ? 'Continuer en popin' : "Demander à l'IA"}</span>
          <span className="topbar-ai-short">IA</span>
        </button>
      </div>
    </header>
  );
}
